import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Divider, Typography, Box, List, ListItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HistoryDTO } from 'common/utils/util.type';
import { getFullProjectHistoryById, getProjectHistory } from '../project/project.api';
import { formatDateTime } from '../../common/utils/common.util';

interface HistoryAccordionProps {
  projectId: string;
  isSaving?: boolean;
}

const HistoryAccordion: React.FC<HistoryAccordionProps> = ({ projectId, isSaving }) => {
  const [history, setHistory] = useState<HistoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [fullContent, setFullContent] = useState<Record<string, string>>({});
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastEntryRef = useCallback(
    (node: Element) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(prevPage => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore],
  );

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);

      // Fetch new paginated history
      const newHistory = await getProjectHistory(projectId, page);

      if (newHistory.length === 0) {
        setHasMore(false);
      } else {
        setHistory(prev => [...prev, ...newHistory]);
      }

      setIsLoading(false);
    };

    fetchHistory();
  }, [projectId, page, isSaving]);

  useEffect(() => {
    if (!isSaving) {
      setPage(1);
      setHistory([]);
    }
  }, [isSaving]);

  const fetchFullContent = async (historyId: string) => {
    try {
      const fullHistory = await getFullProjectHistoryById(historyId);
      if (fullHistory && fullHistory.content) {
        setFullContent(prev => ({ ...prev, [historyId]: fullHistory.content ?? '' }));
        setExpandedEntries(prev => new Set(prev).add(historyId));
      }
    } catch (error) {
      console.error('Error fetching full history content:', error);
    }
  };

  return (
    <Accordion elevation={3} defaultExpanded style={{ marginTop: '5px', borderRadius: '8px' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant='h5' color='primary'>
          Project History
        </Typography>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ flexDirection: 'column', maxHeight: '400px', overflowY: 'auto' }}>
        {isLoading && page === 1 && <Typography>Loading history...</Typography>}

        {!isLoading && history.length === 0 && (
          <Typography variant='body2' color='textSecondary'>
            No history records found.
          </Typography>
        )}

        {history.map((entry, index) => {
          const isExpanded = expandedEntries.has(entry.id);
          const displayedContent = isExpanded ? fullContent[entry.id] || entry.content : entry.summary;

          let parsedMessages: { Section: string; DrawName?: string; Message: string }[] = [];
          try {
            parsedMessages = JSON.parse(displayedContent ?? '[]');
          } catch (error) {
            console.error('Error parsing history summary:', error);
          }

          const projectMessages = parsedMessages.filter(msg => msg.Section === 'Project');
          const projectSummaryMessages = parsedMessages.filter(msg => msg.Section === 'projectSummary');
          const groupedByDraw = parsedMessages
            .filter(msg => msg.Section !== 'projectSummary')
            .reduce(
              (acc, msg) => {
                if (!msg.DrawName) return acc;
                if (!acc[msg.DrawName]) acc[msg.DrawName] = [];
                acc[msg.DrawName].push(msg);
                return acc;
              },
              {} as Record<string, { Section: string; Message: string }[]>,
            );

          const isTruncated =
            projectSummaryMessages.some(msg => msg.Message.endsWith('...')) ||
            Object.values(groupedByDraw).some(drawMessages => drawMessages.some(msg => msg.Message.endsWith('...')));

          const formattedDate = formatDateTime(entry.createdOn ?? '');
          return (
            <Box
              key={entry.id}
              sx={{ marginBottom: '20px', padding: '8px', borderBottom: '1px solid #ddd' }}
              ref={index === history.length - 1 ? lastEntryRef : null}
            >
              {entry.createdBy && (
                <Typography variant='body2' color='textSecondary'>
                  {formattedDate} - {entry.createdBy.firstName} {entry.createdBy.lastName}
                </Typography>
              )}

              {projectMessages.length > 0 && (
                <>
                  <Typography variant='h6' sx={{ mt: 1 }}>
                    Project
                  </Typography>
                  <List>
                    {projectMessages.map((msg, idx) => {
                      const isLastMessage = idx === projectMessages.length - 1 && msg.Message.endsWith('...');
                      return (
                        <ListItem key={idx}>
                          <Typography variant='body2'>
                            {msg.Message}
                            {isLastMessage && (
                              <Typography
                                component='span'
                                color='primary'
                                sx={{ cursor: 'pointer', ml: 1, fontSize: 15 }}
                                onClick={() => fetchFullContent(entry.id)}
                              >
                                [ view more ]
                              </Typography>
                            )}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}

              {projectSummaryMessages.length > 0 && (
                <>
                  <Typography variant='h6' sx={{ mt: 1 }}>
                    Project Summary
                  </Typography>
                  <List>
                    {projectSummaryMessages.map((msg, idx) => {
                      const isLastMessage = idx === projectSummaryMessages.length - 1 && msg.Message.endsWith('...');
                      return (
                        <ListItem key={idx}>
                          <Typography variant='body2'>
                            {msg.Message}
                            {isLastMessage && (
                              <Typography
                                component='span'
                                color='primary'
                                sx={{ cursor: 'pointer', ml: 1, fontSize: 15 }}
                                onClick={() => fetchFullContent(entry.id)}
                              >
                                [ view more ]
                              </Typography>
                            )}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}

              {Object.entries(groupedByDraw).map(([drawName, messages]) => (
                <Box key={drawName} sx={{ mt: 2 }}>
                  <Typography variant='h6'>{drawName}</Typography>
                  <List>
                    {messages.map((msg, idx) => {
                      const isLastMessage = idx === messages.length - 1 && msg.Message.endsWith('...');
                      return (
                        <ListItem key={idx}>
                          <Typography variant='body2'>
                            {msg.Message}
                            {isLastMessage && (
                              <Typography
                                component='span'
                                color='primary'
                                sx={{ cursor: 'pointer', ml: 1, fontSize: 15 }}
                                onClick={() => fetchFullContent(entry.id)}
                              >
                                [ view more ]
                              </Typography>
                            )}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ))}
            </Box>
          );
        })}

        {isLoading && page > 1 && <Typography>Loading more...</Typography>}
      </AccordionDetails>
    </Accordion>
  );
};

export default HistoryAccordion;
